import { useCallback } from 'react'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import { Select } from '@factorialco/f0-react/dist/experimental'
import { AccessForSelectFragment, AccessSelectorDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

interface HiringTeamSelectorProps {
  hiringManagerId: number
  recruiterId: number | null
  hiringManagerError?: string
  recruiterError?: string
  selectedHiringManagerItem: AccessForSelectFragment | null
  selectedRecruiterItem: AccessForSelectFragment | null
  onHiringManagerChange: (value: number, item: AccessForSelectFragment | null) => void
  onRecruiterChange: (value: number | null, item: AccessForSelectFragment | null) => void
}

export const HiringTeamSelector = ({
  hiringManagerId,
  recruiterId,
  hiringManagerError,
  recruiterError,
  selectedHiringManagerItem,
  selectedRecruiterItem,
  onHiringManagerChange,
  onRecruiterChange,
}: HiringTeamSelectorProps) => {
  const { access } = useCurrentSession()

  const hiringTeamOptions = useGraphqlDataSource(
    {
      paginationType: 'infinite-scroll',
      perPage: 25,
      document: AccessSelectorDocument,
      search: { enabled: true },
      mapData: (data) => data.apiCore.accessesConnection.edges.map((edge) => edge.node),
      mapPagination: (data) => ({
        total: 0,
        cursor: data.apiCore.accessesConnection.pageInfo.endCursor ?? '0',
        hasNextPage: !!data.apiCore.accessesConnection.pageInfo.hasNextPage,
      }),
      mapVariables: ({ search }) => ({
        search,
        excludeIds: [access.id],
      }),
    },
    [access.id]
  )

  const mapOptions = useCallback(
    (item: AccessForSelectFragment) => ({
      type: 'item',
      value: String(item.id),
      label: `${item.firstName} ${item.lastName}`,
      avatar: {
        type: 'person',
        firstName: item.firstName,
        lastName: item.lastName,
        src: buildUrl(item.avatar),
      },
    }),
    []
  )

  return (
    <Grid variant="fixed">
      <Box gridColSpan={{ xxs: 1, xs: 6 }}>
        <Select
          source={hiringTeamOptions}
          mapOptions={mapOptions}
          label={i18n.t('ats.requisitions_form.hiring_manager')}
          hint={i18n.t('ats.requisitions_form.hiring_manager_description')}
          error={hiringManagerError}
          value={hiringManagerId ? String(hiringManagerId) : undefined}
          onChange={(value: string, originalItem?: AccessForSelectFragment) => {
            onHiringManagerChange(value ? Number(value) : 0, originalItem ?? null)
          }}
          defaultItem={
            selectedHiringManagerItem ? mapOptions(selectedHiringManagerItem) : undefined
          }
          showSearchBox
          searchEmptyMessage={i18n.t('ats.jobs.new_job_modal.search_no_results_found')}
          size="md"
        />
      </Box>
      <Box gridColSpan={{ xxs: 1, xs: 6 }}>
        <Select
          source={hiringTeamOptions}
          mapOptions={mapOptions}
          label={i18n.t('ats.requisitions_form.recruiter')}
          error={recruiterError}
          value={recruiterId ? String(recruiterId) : undefined}
          onChange={(value: string, originalItem?: AccessForSelectFragment) => {
            onRecruiterChange(value ? Number(value) : null, originalItem ?? null)
          }}
          defaultItem={selectedRecruiterItem ? mapOptions(selectedRecruiterItem) : undefined}
          showSearchBox
          searchEmptyMessage={i18n.t('ats.jobs.new_job_modal.search_no_results_found')}
          size="md"
        />
      </Box>
    </Grid>
  )
}
